import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { ToastList } from '../ToastList';
import { randomStr } from '../../../Shared/random';
import { getDefaultConfig } from '../ToastHub';
export function useToast(props) {
    const handlerRef = React.createRef();
    const refInternalConfig = useRef({
        ...{
            disablePortal: false,
        },
        ...getDefaultConfig(),
        ...(props ?? {}),
    });
    const { disablePortal } = refInternalConfig.current;
    const refStatuMap = useRef({});
    function createOpen(type, content, duration, onClose) {
        const config = refInternalConfig.current;
        let timeoutId = null;
        let data = {
            type,
            zIndex: config.zIndex,
            duration: config.duration,
            screenReadOnly: config.screenReadOnly,
            width: config.width,
            maxWidth: config.maxWidth,
        };
        if (typeof content === 'string') {
            data = { ...data, content, onClose };
            if (duration !== undefined && duration >= 0) {
                data.duration = duration;
            }
        }
        else {
            data = {
                ...data,
                ...content,
            };
        }
        if (data?.actionable) {
            data.duration = 0;
        }
        const toastData = { ...{ key: randomStr() }, ...data };
        handlerRef.current?.appendToasts([toastData]);
        const delayToast = () => {
            if (toastData.duration !== undefined && toastData.duration > 0) {
                timeoutId = window.setTimeout(() => {
                    handlerRef?.current?.destroyToast?.(toastData.key);
                    timeoutId = null;
                }, toastData.duration);
                refStatuMap.current[toastData.key] = {
                    duration,
                    timeoutId,
                };
            }
        };
        delayToast();
        const toastInstanceDestroy = () => {
            handlerRef?.current?.closeToast?.(toastData.key);
        };
        const toastInstanceClose = () => {
            handlerRef?.current?.closeToast?.(toastData.key);
        };
        const toastInstanceUpdate = (opts) => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
            handlerRef?.current?.updateToast?.(toastData.key, opts);
            delayToast();
        };
        return {
            destroy: toastInstanceDestroy,
            close: toastInstanceClose,
            update: toastInstanceUpdate,
        };
    }
    const hookAPI = React.useMemo(() => ({
        info(content, duration, onClose) {
            return createOpen('info', content, duration, onClose);
        },
        success(content, duration, onClose) {
            return createOpen('success', content, duration, onClose);
        },
        error(content, duration, onClose) {
            return createOpen('error', content, duration, onClose);
        },
        warn(content, duration, onClose) {
            return createOpen('warn', content, duration, onClose);
        },
        warning(content, duration, onClose) {
            return createOpen('warning', content, duration, onClose);
        },
        destroy(key) {
            handlerRef?.current?.destroyToast?.(key);
        },
        close(key) {
            handlerRef?.current?.closeToast?.(key);
        },
        config(config) {
            const newConfig = {
                ...refInternalConfig.current,
                ...config,
            };
            refInternalConfig.current = newConfig;
            handlerRef.current?.updateConfig(newConfig);
        },
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    let toastList = (React.createElement(ToastList, { defaultConfig: refInternalConfig.current, handlerRef: handlerRef }));
    if (!disablePortal) {
        toastList = createPortal(toastList, document.body);
    }
    return [hookAPI, toastList];
}
